<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Microscope, LogIn, Building2, AlertCircle } from 'lucide-vue-next'
import { useUserStore } from '@/stores'
import { $alert } from '@/plugins/simple-alert'
import AccountRequestModal from '@/components/AccountRequestModal.vue'

const router = useRouter()
const userStore = useUserStore()

import loginBg from '@/assets/login-bg.jpg'

const email = ref('')
const password = ref('')
const rememberId = ref(false)
const showAccountRequestModal = ref(false)

const emailInputRef = ref<HTMLInputElement | null>(null)
const passwordInputRef = ref<HTMLInputElement | null>(null)

const emailError = ref('')
const passwordError = ref('')
const loginError = ref('')

const bgImage = loginBg

const isSubmitting = ref(false)

onMounted(() => {
  const savedId = localStorage.getItem('saved_user_id')
  if (savedId) {
    email.value = savedId
    rememberId.value = true
  }
})

function openAccountRequestModal() {
  loginError.value = ''
  showAccountRequestModal.value = true
}

async function handleSubmit(e: Event) {
  e.preventDefault()
  if (isSubmitting.value) return
  isSubmitting.value = true
  emailError.value = ''
  passwordError.value = ''
  loginError.value = ''

  try {
    if (!email.value || !email.value.trim()) {
      emailError.value = '아이디를 입력해주세요.'
      emailInputRef.value?.focus()
      return
    }
    if (!password.value) {
      passwordError.value = '비밀번호를 입력해주세요.'
      passwordInputRef.value?.focus()
      return
    }

    const userEmail = email.value.trim()
    const result = await userStore.login(userEmail, password.value)
    if (result.success) {
      if (rememberId.value) {
        localStorage.setItem('saved_user_id', userEmail)
      } else {
        localStorage.removeItem('saved_user_id')
      }

      // 2026-09: 협력파트너도 HOME 메뉴가 열려 있으므로(role_menu_mapping.ts) 로그인 직후
      // 기본 랜딩 페이지도 다른 역할과 동일하게 Home('/')으로 통일한다. router/index.ts의
      // beforeEach 가드에도 동일한 리다이렉트 규칙이 있지만, 그건 "이미 로그인된 상태에서
      // /login에 접근"할 때만 타는 것이고 실제 로그인 폼 제출은 이 코드가 처리하므로
      // 여기도 같이 고쳐야 한다.
      const role = (userStore.currentUser.role || '').toUpperCase()
      if (role === 'ADMIN') {
        router.push('/dashboard')
      } else {
        router.push('/')
      }
    } else {
      const msg = result.errorMsg || '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.'
      loginError.value = msg
      passwordInputRef.value?.focus()
      passwordInputRef.value?.select()
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="h-screen flex flex-col relative bg-cover bg-center bg-no-repeat overflow-hidden"
    :style="{ backgroundImage: `url(${bgImage})` }">
    <!-- Dark Overlay -->
    <div class="absolute inset-0 bg-slate-950/70 pointer-events-none z-0" />

    <main class="relative z-10 flex-grow flex items-center justify-center px-4 py-6">
      <div class="w-full max-w-[500px] login-card-appear">
        <!-- Login Card -->
        <div class="bg-white rounded-2xl shadow-2xl overflow-hidden p-8 md:p-10 border border-white/10">
          <!-- Logo & Header -->
          <div class="flex flex-col items-center mb-8 text-center">
            <div class="mb-4 flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl border border-blue-100">
              <Microscope class="text-[#145da0] w-8 h-8" />
            </div>
            <div class="space-y-1">
              <span class="text-[10px] font-bold tracking-[0.3em] text-blue-800 uppercase">KRISS</span>
              <h1 class="text-xl md:text-2xl font-bold tracking-tight text-[#145da0]">
                전자현미경 연구 데이터 플랫폼
              </h1>
              <p class="text-slate-500 text-xs md:text-sm mt-1 font-medium">
                Korea Research Institute of Standards and Science
              </p>
            </div>
          </div>

          <!-- Login Error Alert Banner -->
          <div v-if="loginError" class="mb-5 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-3 text-rose-700 text-xs font-semibold shadow-sm">
            <AlertCircle class="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <div class="flex-1 min-w-0">
              <p class="font-bold text-rose-800 text-sm mb-1">로그인 실패</p>
              <div class="text-rose-600 font-medium leading-relaxed max-h-32 overflow-y-auto whitespace-pre-wrap break-all pr-1 login-error-scroll">
                {{ loginError }}
              </div>
            </div>
          </div>

          <!-- Form -->
          <form class="space-y-4" @submit="handleSubmit">
            <div class="space-y-1">
              <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1" for="email">
                User ID / Email
              </label>
              <input id="email" ref="emailInputRef" v-model="email" name="email" type="text" placeholder="admin"
                :class="[
                  'w-full px-4 py-3 bg-slate-50 rounded-xl border transition-all outline-none text-slate-900 placeholder:text-slate-400 text-sm',
                  emailError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600'
                ]"
                @input="emailError = ''" />
              <p v-if="emailError" class="text-xs text-rose-500 font-semibold mt-1 ml-1">
                {{ emailError }}
              </p>
            </div>
            <div class="space-y-1">
              <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1" for="password">
                Password
              </label>
              <input id="password" ref="passwordInputRef" v-model="password" name="password" type="password" placeholder="••••••••"
                :class="[
                  'w-full px-4 py-3 bg-slate-50 rounded-xl border transition-all outline-none text-slate-900 placeholder:text-slate-400 text-sm',
                  passwordError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600'
                ]"
                @input="passwordError = ''" />
              <p v-if="passwordError" class="text-xs text-rose-500 font-semibold mt-1 ml-1">
                {{ passwordError }}
              </p>
            </div>

            <div class="flex items-center justify-between py-0.5">
              <label class="flex items-center cursor-pointer group">
                <input v-model="rememberId" type="checkbox"
                  class="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition-all" />
                <span class="ml-2 text-xs text-slate-600 group-hover:text-slate-900 transition-colors font-medium">
                  Remember ID
                </span>
              </label>
              <a class="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors" href="#">
                비밀번호 찾기
              </a>
            </div>

            <button type="submit"
              :disabled="isSubmitting"
              :class="[
                'w-full h-12 bg-[#145da0] text-white font-bold rounded-xl shadow-lg hover:bg-[#114d85] active:scale-[0.98] transition-all tracking-wide flex items-center justify-center gap-2 text-base',
                isSubmitting ? 'opacity-60 cursor-not-allowed' : ''
              ]">
              <LogIn class="w-4 h-4" />
              로그인 (Sign In)
            </button>
          </form>

          <!-- Help Link -->
          <div class="mt-8 pt-5 border-t border-slate-50 text-center">
            <p class="text-xs text-slate-500 font-medium">
              계정이 없으신가요?
              <button type="button" @click="openAccountRequestModal" class="font-bold text-blue-600 hover:underline underline-offset-4 cursor-pointer ml-1">
                시스템 관리자에게 문의하세요 (계정 신청)
              </button>
            </p>
          </div>
        </div>
      </div>
    </main>

    <!-- Account Request Modal -->
    <AccountRequestModal :is-open="showAccountRequestModal" @close="showAccountRequestModal = false" />

    <!-- Footer -->
    <footer
      class="relative z-10 flex flex-col md:flex-row justify-between items-center w-full px-6 py-6 md:px-12 bg-slate-950/40 backdrop-blur-md border-t border-white/5 mt-auto">
      <div class="mb-4 md:mb-0 text-center md:text-left">
        <div class="flex items-center justify-center md:justify-start gap-2 mb-1">
          <Building2 class="text-white/90 w-4 h-4" />
          <span class="text-base font-bold text-white tracking-tight">한국표준과학연구원</span>
        </div>
        <p class="text-[10px] leading-relaxed text-white/50 max-w-xl">
          본 플랫폼은 한국표준과학연구원(KRISS)의 공식 연구 데이터 관리 시스템입니다.<br />
          © 2024 Korea Research Institute of Standards and Science. All rights reserved.
        </p>
      </div>
      <nav class="flex flex-wrap justify-center gap-4 md:gap-8">
        <a class="text-[9px] font-bold text-white/50 hover:text-white uppercase tracking-widest transition-all"
          href="#">이용약관</a>
        <a class="text-[9px] font-bold text-white/50 hover:text-white uppercase tracking-widest transition-all"
          href="#">개인정보처리방침</a>
        <a class="text-[9px] font-bold text-white/50 hover:text-white uppercase tracking-widest transition-all"
          href="#">보안 정책</a>
        <a class="text-[9px] font-bold text-white/50 hover:text-white uppercase tracking-widest transition-all"
          href="#">기술 지원</a>
      </nav>
    </footer>
  </div>
</template>

<style scoped>
.login-card-appear {
  animation: loginFadeIn 0.6s ease-out forwards;
}

@keyframes loginFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-error-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(244, 63, 94, 0.4) transparent;
}

.login-error-scroll::-webkit-scrollbar {
  width: 4px;
}

.login-error-scroll::-webkit-scrollbar-track {
  background: rgba(244, 63, 94, 0.08);
  border-radius: 4px;
}

.login-error-scroll::-webkit-scrollbar-thumb {
  background: rgba(244, 63, 94, 0.4);
  border-radius: 4px;
}

.login-error-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(244, 63, 94, 0.7);
}
</style>
